import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {I18n} from 'react-redux-i18n';
import {v4 as uuid} from 'uuid';
import _isEqual from 'lodash/isEqual';
import _findIndex from 'lodash/findIndex';

import {withStyles} from '@material-ui/core/styles';

import {CustomInput} from '../../material-dashboard-pro-react/components/index';
import {Switch, Dropdown, RenderOrEmpty} from '../../components';
import RelayStatusControl from '../../components/RelayStatusControl/RelayStatusControl';

import {
  getEditableRelayInfo,
  resetRelayEditFormData,
  updateRelayInfo,
} from './action';
import {
  adoptRelay,
  deleteRelay,
} from '../CampusRelaysTab/action';

import {RELAY_STATUSES, STATES_ENTITY} from '../../constants';

import style from './style';

const formControlProps = {
  fullWidth: true,
};

class RelayEditForm extends Component {
  static shouldUpdateRelayObject(prevProps, props) {
    return !_isEqual(prevProps.selectedRelayTableRowData, props.selectedRelayTableRowData);
  }

  static shouldResetFormData(prevProps, props) {
    return !_isEqual(prevProps.editMode, props.editMode)
      && _isEqual(props.relayState, STATES_ENTITY.EDITING_CANCELED);
  }

  componentDidMount() {
    this.props.getEditableRelayInfo(this.props.selectedRelayTableRowData);
  }

  shouldComponentUpdate(nextProps) {
    return !_isEqual(
      this.props.selectedRelayTableRowData,
      nextProps.selectedRelayTableRowData,
    )
      || !_isEqual(this.props.editMode, nextProps.editMode)
      || !_isEqual(this.props.editableRelayInfo, nextProps.editableRelayInfo);
  }

  componentDidUpdate(prevProps) {
    if (RelayEditForm.shouldUpdateRelayObject(prevProps, this.props)
      || RelayEditForm.shouldResetFormData(prevProps, this.props)) {
      this.props.getEditableRelayInfo(this.props.selectedRelayTableRowData);
    }
  }

  componentWillUnmount() {
    this.props.resetRelayEditFormData();
  }

  getUnmodifiableInputProps = (fieldName) => ({
    disabled: true,
    value: this.props.editableRelayInfo[fieldName] || '—',
    name: fieldName,
    type: 'text',
  });

  getPoliciesOptions = () => [
    ...this.props.policies.map((policy) => policy.name),
  ];

  getSelectedPolicyIndex = () => {
    const {editableRelayInfo, policies} = this.props;

    return _findIndex(
      policies,
      (item) => _isEqual(item.id, editableRelayInfo.policyId),
    );
  };

  handleTextSettingChange = (e, settingName) => {
    this.props.updateRelayInfo(
      {
        ...this.props.editableRelayInfo,
        [settingName]: e.target.value,
      },
      true,
    );
  };

  handleBoolSettingChange = (e, settingName) => {
    this.props.updateRelayInfo(
      {
        ...this.props.editableRelayInfo,
        [settingName]: e.target.checked,
      },
      true,
    );
  };

  handleAdoptRelay = () => {
    const {editableRelayInfo} = this.props;

    this.props.adoptRelay(editableRelayInfo.id);
    this.props.updateRelayInfo(
      {
        ...editableRelayInfo,
        adopted: true,
      },
      true,
    );
  };

  handleChangeDynamicWanPolicy = (e) => {
    const {
      editableRelayInfo,
      policies,
    } = this.props;
    const index = e.target.value;
    this.props.updateRelayInfo(
      {
        ...editableRelayInfo,
        policyName: policies[index].name,
        policyId: policies[index].id,
      },
      true,
    );
  };

  handleForgetRelay = () => {
    this.props.deleteRelay(this.props.editableRelayInfo.id);
    this.props.closeForm();
  };

  render() {
    const {classes, editableRelayInfo, editMode} = this.props;

    return (
      <div className={classes.form}>
        <div>
          <CustomInput
            labelText={I18n.t('campusPage.relaysTab.labels.name')}
            id={uuid()}
            formControlProps={formControlProps}
            inputProps={{
              name: 'name',
              value: editableRelayInfo.name,
              onChange: (e) => this.handleTextSettingChange(e, 'name'),
              disabled: !editMode,
            }}
          />
        </div>
        <div>
          <CustomInput
            labelText={I18n.t('campusPage.relaysTab.labels.description')}
            id={uuid()}
            formControlProps={formControlProps}
            inputProps={{
              name: 'description',
              value: editableRelayInfo.description,
              onChange: (e) => this.handleTextSettingChange(e, 'description'),
              disabled: !editMode,
            }}
          />
        </div>
        <div className={classes.switchContainer}>
          <Switch
            isChecked={editableRelayInfo.dynamicWan}
            onChange={(e) => this.handleBoolSettingChange(e, 'dynamicWan')}
            label={I18n.t('campusPage.relaysTab.labels.enableDynamicWan')}
            disabled={!editMode}
            labelFormClassName="formControl_oneLine"
            labelClassName="switch__label_noPadding"
          />
        </div>
        <div>
          <RenderOrEmpty isShow={!editableRelayInfo.dynamicWan}>
            <CustomInput
              labelText={I18n.t('campusPage.relaysTab.labels.network')}
              id={uuid()}
              formControlProps={formControlProps}
              inputProps={this.getUnmodifiableInputProps('network')}
            />
          </RenderOrEmpty>
        </div>
        <div>
          <RenderOrEmpty isShow={editableRelayInfo.dynamicWan}>
            <Dropdown
              customSelectClass="dropdown__select_policySettingsTab"
              disabled={!editMode}
              dropdownName="policy"
              fullWidth={true}
              label={I18n.t('campusPage.relaysTab.labels.dynamicWanPolicy')}
              onChangeValue={this.handleChangeDynamicWanPolicy}
              options={this.getPoliciesOptions()}
              selectedItemIndex={this.getSelectedPolicyIndex()}
            />
          </RenderOrEmpty>
        </div>
        <div>
          <CustomInput
            labelText={I18n.t('campusPage.relaysTab.labels.version')}
            id={uuid()}
            inputProps={this.getUnmodifiableInputProps('version')}
          />
        </div>
        <div>
          <RelayStatusControl
            relayId={editableRelayInfo.id}
            editableRelayInfo={editableRelayInfo}
            label={I18n.t('campusPage.relaysTab.labels.actions')}
            adoptRelay={this.handleAdoptRelay}
            forgetRelay={this.handleForgetRelay}
          />
        </div>
        <div>
          <CustomInput
            labelText={I18n.t('campusPage.relaysTab.labels.lastConfigUpdatingAt')}
            id={uuid()}
            formControlProps={formControlProps}
            inputProps={this.getUnmodifiableInputProps('lastConfigUpdatingAt')}
          />
        </div>
      </div>
    );
  }
}

RelayEditForm.propTypes = {
  classes: PropTypes.object.isRequired,
  editMode: PropTypes.bool.isRequired,
  selectedRelayTableRowData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    network: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    lastConfigUpdatingAt: PropTypes.string,
    status: PropTypes.oneOf(Object.keys(RELAY_STATUSES)).isRequired,
  }).isRequired,

  // This prop is used in 'shouldResetFormData' helper function only,
  // so ESLint thinks that it is unused:
  // eslint-disable-next-line react/no-unused-prop-types
  relayState: PropTypes.string.isRequired,

  editableRelayInfo: PropTypes.object.isRequired,

  policies: PropTypes.array.isRequired,

  getEditableRelayInfo: PropTypes.func.isRequired,
  resetRelayEditFormData: PropTypes.func.isRequired,
  updateRelayInfo: PropTypes.func.isRequired,
  deleteRelay: PropTypes.func.isRequired,
  closeForm: PropTypes.func.isRequired,
  adoptRelay: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  editMode: state.campusRelaysTabReducer.editMode,
  selectedRelayTableRowData: state.campusRelaysTabReducer.selectedRelayTableRowData,
  relayState: state.campusRelaysTabReducer.relayState,

  policies: state.campusReducer.policies,

  editableRelayInfo: state.relayEditFormReducer.editableRelayInfo,
});

const mapDispatchToProps = (dispatch) => ({
  getEditableRelayInfo: bindActionCreators(getEditableRelayInfo, dispatch),
  resetRelayEditFormData: bindActionCreators(resetRelayEditFormData, dispatch),
  updateRelayInfo: bindActionCreators(updateRelayInfo, dispatch),
  adoptRelay: bindActionCreators(adoptRelay, dispatch),
  deleteRelay: bindActionCreators(deleteRelay, dispatch),
});

const RelayEditFormWithStyles = withStyles(style)(RelayEditForm);

export default connect(mapStateToProps, mapDispatchToProps)(RelayEditFormWithStyles);
